import React, { useState, useEffect } from 'react';
import { Table, Card, Form, Input, Select, Button, message, Space, Drawer, Modal, Transfer, Popconfirm, Tabs, Switch, Row, Col, List, Checkbox, Tag, Pagination, Tooltip } from 'antd';
import { ExclamationCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { getCustomerLinksList, createCustomerLink, updateCustomerLinkName, deleteCustomerLink, getCustomerLinkDetail, getGroupList, createGroup, updateGroup, deleteGroup } from '../../../api/customerLinks';
import { getCorpList } from '../../../api/corp';
import { getEmployeesList } from '../../../api/employee';
import { copyToClipboard } from '../../../utils/clipboard';
import { useSuite } from '../../../context/SuiteContext';
import './CustomerLinksList.css';

const { Option } = Select;

const CustomerLinksList = () => {
    const { suiteId } = useSuite();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [corpList, setCorpList] = useState([]);

    // Create drawer states
    const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [createLinkName, setCreateLinkName] = useState('');
    const [currentGroupName, setCurrentGroupName] = useState('');
    const [currentGroupId, setCurrentGroupId] = useState(null);

    // Split pane states
    const [selectedCorpId, setSelectedCorpId] = useState(null);
    const [selectedUsersMap, setSelectedUsersMap] = useState({}); // { [corpId]: [userIds] }

    // Create Corp List State
    const [createCorpList, setCreateCorpList] = useState([]);
    const [createCorpTotal, setCreateCorpTotal] = useState(0);
    const [createCorpPage, setCreateCorpPage] = useState(1);
    const [createCorpLoading, setCreateCorpLoading] = useState(false);
    const [createCorpSearchName, setCreateCorpSearchName] = useState('');

    // Create Employee List State
    const [createEmployeeList, setCreateEmployeeList] = useState([]);
    const [createEmployeeTotal, setCreateEmployeeTotal] = useState(0);
    const [createEmployeePage, setCreateEmployeePage] = useState(1);
    const [createEmployeeLoading, setCreateEmployeeLoading] = useState(false);
    const [createEmployeeSearchName, setCreateEmployeeSearchName] = useState('');

    // Update name modal states
    const [updateModalVisible, setUpdateModalVisible] = useState(false);
    const [currentLink, setCurrentLink] = useState(null);
    const [updateLoading, setUpdateLoading] = useState(false);

    // Detail drawer states
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const [form] = Form.useForm();
    const [updateForm] = Form.useForm();

    // Fetch corp list
    const fetchCorpList = async () => {
        try {
            const res = await getCorpList({ suite_id: suiteId });
            if (res.code === 0) {
                setCorpList(res.data.list);
                // Set default corp in search form
                if (res.data.list.length > 0) {
                    form.setFieldsValue({ corp_id: res.data.list[0].corp_id });
                }
            } else {
                message.error(res.message || '获取企业列表失败');
            }
        } catch (error) {
            console.error('Fetch corp list failed:', error);
            message.error(error.message || '获取企业列表失败');
        }
    };

    // Fetch customer links
    const fetchLinks = async (page = 1, pageSize = 10, groupId = currentGroupId) => {
        setLoading(true);
        try {
            const values = await form.validateFields();
            const params = {
                page,
                page_size: pageSize,
                suite_id: suiteId,
                group_id: groupId, // Ensure group_id is passed
                ...values,
            };

            // Remove empty values
            Object.keys(params).forEach(key => {
                if (params[key] === undefined || params[key] === '') {
                    delete params[key];
                }
            });

            const res = await getCustomerLinksList(params);
            if (res.code === 0) {
                setData(res.data.list);
                setPagination({
                    ...pagination,
                    current: res.data.page,
                    pageSize: res.data.page_size,
                    total: res.data.total,
                });
            } else {
                message.error(res.message || '获取链接列表失败');
            }
        } catch (error) {
            console.error('Fetch links failed:', error);
            message.error(error.message || '获取链接列表失败');
        } finally {
            setLoading(false);
        }
    };



    // Link List Drawer State
    const [linkListDrawerVisible, setLinkListDrawerVisible] = useState(false);

    // Group states
    const [groupData, setGroupData] = useState([]);
    const [groupLoading, setGroupLoading] = useState(false);
    const [groupPagination, setGroupPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [groupForm] = Form.useForm(); // Search form
    const [createGroupModalVisible, setCreateGroupModalVisible] = useState(false);
    const [editGroupModalVisible, setEditGroupModalVisible] = useState(false);
    const [groupModalLoading, setGroupModalLoading] = useState(false);
    const [currentGroup, setCurrentGroup] = useState(null);
    const [groupEditForm] = Form.useForm(); // Create/Edit form

    useEffect(() => {
        if (suiteId) {
            fetchCorpList();
        }
    }, [suiteId]);

    useEffect(() => {
        if (suiteId && corpList.length > 0) {
            fetchGroups();
        }
    }, [suiteId, corpList]);

    // Group functions
    const fetchGroups = async (page = 1, pageSize = 10) => {
        setGroupLoading(true);
        try {
            const values = await groupForm.validateFields();
            const params = {
                page,
                page_size: pageSize,
                suite_id: suiteId,
                ...values,
            };
            // Default simple handling for now as no complex filters
            if (!params.group_name) delete params.group_name;

            const res = await getGroupList(params);
            if (res.code === 0) {
                setGroupData(res.data.list);
                setGroupPagination({
                    ...groupPagination,
                    current: res.data.page || page,
                    pageSize: res.data.page_size || pageSize,
                    total: res.data.total,
                });
            } else {
                message.error(res.message || '获取分组列表失败');
            }
        } catch (error) {
            console.error('Fetch groups failed:', error);
            message.error(error.message || '获取分组列表失败');
        } finally {
            setGroupLoading(false);
        }
    };

    const handleGroupTableChange = (newPagination) => {
        fetchGroups(newPagination.current, newPagination.pageSize);
    };

    const handleGroupSearch = () => {
        fetchGroups(1, groupPagination.pageSize);
    };

    const handleGroupReset = () => {
        groupForm.resetFields();
        fetchGroups(1, groupPagination.pageSize);
    };

    const handleCreateGroupClick = () => {
        setCurrentGroup(null);
        groupEditForm.resetFields();
        setCreateGroupModalVisible(true);
    };

    const handleEditGroupClick = (record) => {
        setCurrentGroup(record);
        groupEditForm.setFieldsValue({
            group_name: record.group_name,
            remark: record.remark
        });
        setEditGroupModalVisible(true);
    };

    const handleGroupSubmit = async () => {
        try {
            const values = await groupEditForm.validateFields();
            setGroupModalLoading(true);

            let res;
            if (currentGroup) {
                // Update
                res = await updateGroup({
                    id: currentGroup.id,
                    suite_id: suiteId,
                    ...values
                });
            } else {
                // Create
                res = await createGroup({
                    suite_id: suiteId,
                    ...values
                });
            }

            if (res.code === 0) {
                message.success(currentGroup ? '修改成功' : '创建一个成功');
                setCreateGroupModalVisible(false);
                setEditGroupModalVisible(false);
                fetchGroups(groupPagination.current, groupPagination.pageSize);
            } else {
                message.error(res.message || '操作失败');
            }
        } catch (error) {
            console.error('Group submit failed:', error);
            message.error(error.message || '操作失败');
        } finally {
            setGroupModalLoading(false);
        }
    };

    const handleDeleteGroup = async (record) => {
        try {
            const res = await deleteGroup({ id: record.id });
            if (res.code === 0) {
                message.success('删除成功');
                fetchGroups(groupPagination.current, groupPagination.pageSize);
            } else {
                message.error(res.message || '删除失败');
            }
        } catch (error) {
            console.error('Delete group failed:', error);
            message.error('删除失败');
        }
    };

    const handleGroupStatusChange = async (checked, record) => {
        const newStatus = checked ? 1 : -1;
        try {
            const res = await updateGroup({
                id: record.id,
                status: newStatus
            });
            if (res.code === 0) {
                message.success('状态更新成功');
                fetchGroups(groupPagination.current, groupPagination.pageSize);
            } else {
                message.error(res.message || '状态更新失败');
            }
        } catch (error) {
            console.error('Update status failed:', error);
            message.error('状态更新失败');
        }
    };

    // Link List Drawer triggered by CS Total click
    const handleCSClick = (record) => {
        // Just open the drawer as requested.
        setCurrentGroupName(record.group_name || '');
        setCurrentGroupId(record.id);
        setLinkListDrawerVisible(true);
        // Pre-fill search form or just list all?
        form.resetFields();
        // Pass record.id directly to fetch because setState is async
        fetchLinks(1, pagination.pageSize, record.id);
    };

    const groupColumns = [
        { title: '获客分组', dataIndex: 'group_name', key: 'group_name' },
        // { title: '关联企业', dataIndex: 'corp_total', key: 'corp_total' },
        {
            title: '关联企业',
            dataIndex: 'corp_total',
            key: 'corp_total',
            align: 'center',
            render: (text, record) => (
                <span className="link-count" onClick={() => handleCSClick(record)}>
                    {text}
                </span>
            )
        },
        {
            title: '关联客服',
            dataIndex: 'cs_total',
            key: 'cs_total',
            align: 'center',
            render: (text, record) => (
                <span className="link-count" onClick={() => handleCSClick(record)}>
                    {text}
                </span>
            )
        },
        {
            title: '统一加粉链接',
            dataIndex: 'pub_url',
            key: 'pub_url',
            width: 250,
            render: (text) => (
                <div style={{ display: 'flex', alignItems: 'center', maxWidth: 220 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 4, flex: 1 }} title={text}>
                        {text || '-'}
                    </div>
                    {text && (
                        <CopyOutlined
                            style={{ cursor: 'pointer', color: '#1890ff' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(text);
                            }}
                        />
                    )}
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => (
                <Switch
                    className={status === 1 ? 'orange-switch' : ''}
                    checked={status === 1}
                    onChange={(checked) => handleGroupStatusChange(checked, record)}
                    checkedChildren="启用"
                    unCheckedChildren="禁用"
                />
            )
        },
        { title: '加粉数', dataIndex: 'fans_total', key: 'fans_total', align: 'center' },
        { title: '创建时间', dataIndex: 'create_at', key: 'create_at' },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <span className="action-link" onClick={() => handleEditGroupClick(record)}>修改</span>
                    <Popconfirm title="确定删除？" onConfirm={() => handleDeleteGroup(record)}>
                        <span className="action-link" style={{ color: 'red' }}>删除</span>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const handleTableChange = (newPagination) => {
        fetchLinks(newPagination.current, newPagination.pageSize);
    };

    const onFinish = () => {
        fetchLinks(1, pagination.pageSize);
    };

    const onReset = () => {
        form.resetFields();
        fetchLinks(1, pagination.pageSize);
    };

    // Check if corp is in trial period (within 7 days before license expires)
    const isInTrialPeriod = (licenseCheckTime) => {
        if (!licenseCheckTime) return false;
        const now = Date.now() / 1000; // Convert to seconds
        // const trialEndTime = licenseCheckTime - 604800; // 7 days in seconds
        const trialEndTime = licenseCheckTime; // Just use licenseCheckTime directly
        return now <= trialEndTime;
    };

    // Create link handlers
    const fetchCreateCorpList = async (page = 1, searchName = '') => {
        setCreateCorpLoading(true);
        try {
            const params = {
                page,
                page_size: 20, // Smaller page size for split view
                suite_id: suiteId,
                corp_name: searchName
            };
            // Clean params
            Object.keys(params).forEach(key => {
                if (params[key] === undefined || params[key] === '') {
                    delete params[key];
                }
            });

            const res = await getCorpList(params);
            if (res.code === 0) {
                setCreateCorpList(res.data.list || []);
                setCreateCorpTotal(res.data.total || 0);
                setCreateCorpPage(page);

                // Select first VALID corp if none selected and list not empty
                if (!selectedCorpId && res.data.list && res.data.list.length > 0) {
                    const firstValidCorp = res.data.list.find(item => !item.customer_assistant_errcode);
                    if (firstValidCorp) {
                        handleCorpSelect(firstValidCorp.corp_id);
                    }
                }
            } else {
                message.error(res.message || '获取企业列表失败');
            }
        } catch (error) {
            console.error('Fetch create corp list failed:', error);
        } finally {
            setCreateCorpLoading(false);
        }
    };

    const fetchCreateEmployeeList = async (corpId, page = 1, searchName = '') => {
        if (!corpId) return;
        setCreateEmployeeLoading(true);
        try {
            const params = {
                suite_id: suiteId,
                corp_id: corpId,
                group_id: currentGroupId,
                page: page,
                pageSize: 20,
                code_status: 2, // Forced filter
                user_name: searchName
            };
            // Clean params
            Object.keys(params).forEach(key => {
                if (params[key] === undefined || params[key] === '') {
                    delete params[key];
                }
            });

            const res = await getEmployeesList(params);
            if (res.code === 0) {
                setCreateEmployeeList(res.data?.list || []);
                setCreateEmployeeTotal(res.data?.total || 0);
                setCreateEmployeePage(page);
            } else {
                message.error(res.message || '获取员工列表失败');
            }
        } catch (error) {
            console.error('Fetch create employee list failed:', error);
        } finally {
            setCreateEmployeeLoading(false);
        }
    };

    const handleCreateClick = () => {
        setCreateDrawerVisible(true);
        setCreateLinkName('');
        setSelectedUsersMap({});
        setSelectedCorpId(null);
        setCreateCorpSearchName('');
        setCreateEmployeeSearchName('');
        fetchCreateCorpList(1, '');
    };

    const handleCreateClose = () => {
        setCreateDrawerVisible(false);
        setCreateLinkName('');
        setSelectedUsersMap({});
    };

    const handleCorpSelect = (corpId) => {
        setSelectedCorpId(corpId);
        setCreateEmployeeSearchName('');
        fetchCreateEmployeeList(corpId, 1, '');
    };

    const handleUserSelect = (userids, corpId) => {
        setSelectedUsersMap(prev => ({
            ...prev,
            [corpId]: userids
        }));
    };

    // Toggle single user
    const toggleUserSelection = (userid, corpId) => {
        const currentSelected = selectedUsersMap[corpId] || [];
        const isSelected = currentSelected.includes(userid);
        let newSelected;
        if (isSelected) {
            newSelected = currentSelected.filter(id => id !== userid);
        } else {
            newSelected = [...currentSelected, userid];
        }

        setSelectedUsersMap(prev => ({
            ...prev,
            [corpId]: newSelected
        }));
    };

    const handleCreateSubmit = async () => {
        // if (!createLinkName.trim()) {
        //     message.warning('请输入获客链接名称');
        //     return;
        // }

        // Construct users array
        const users = [];
        Object.keys(selectedUsersMap).forEach(corpId => {
            const userids = selectedUsersMap[corpId];
            if (userids && userids.length > 0) {
                users.push({
                    corp_id: corpId,
                    suite_id: suiteId,
                    userid: userids
                });
            }
        });

        if (users.length === 0) {
            message.warning('请至少选择一个客服');
            return;
        }

        setCreateLoading(true);
        try {
            const payload = {
                link_name: createLinkName,
                group_id: currentGroupId, // Required parameter
                users: users
            };

            const res = await createCustomerLink(payload);
            if (res.code === 0) {
                message.success('创建成功');
                handleCreateClose();
                fetchLinks(1, pagination.pageSize);
            } else {
                message.error(res.message || '创建失败');
            }
        } catch (error) {
            console.error('Create link failed:', error);
            message.error(error.message || '创建失败');
        } finally {
            setCreateLoading(false);
        }
    };

    // Update name handlers
    const handleUpdateClick = (record) => {
        setCurrentLink(record);
        updateForm.setFieldsValue({ link_name: record.link_name });
        setUpdateModalVisible(true);
    };

    const handleUpdateSubmit = async () => {
        try {
            const values = await updateForm.validateFields();
            setUpdateLoading(true);

            const res = await updateCustomerLinkName({
                link_id: currentLink.link_id,
                link_name: values.link_name
            });

            if (res.code === 0) {
                message.success('修改成功');
                setUpdateModalVisible(false);
                fetchLinks(pagination.current, pagination.pageSize);
            } else {
                message.error(res.message || '修改失败');
            }
        } catch (error) {
            console.error('Update link name failed:', error);
            message.error(error.message || '修改失败');
        } finally {
            setUpdateLoading(false);
        }
    };

    // Delete handler
    const handleDelete = async (record) => {
        try {
            const res = await deleteCustomerLink({ link_id: record.link_id });
            if (res.code === 0) {
                message.success('删除成功');
                fetchLinks(pagination.current, pagination.pageSize);
            } else {
                message.error(res.message || '删除失败');
            }
        } catch (error) {
            console.error('Delete link failed:', error);
            message.error('删除失败');
        }
    };

    // Detail handler
    const handleDetailClick = async (record) => {
        setDetailData(null); // Clear previous data
        setDetailDrawerVisible(true);
        setDetailLoading(true);
        try {
            const res = await getCustomerLinkDetail({ link_id: record.link_id });
            console.log('Detail response:', res); // Debug log
            if (res.code === 0) {
                console.log('Setting detail data:', res.data); // Debug log
                setDetailData(res.data);
            } else {
                message.error(res.message || '获取详情失败');
            }
        } catch (error) {
            console.error('Fetch detail failed:', error);
            message.error(error.message || '获取详情失败');
        } finally {
            setDetailLoading(false);
        }
    };

    const columns = [
        {
            title: '客服信息',
            key: 'cs_info',
            render: (_, record) => (
                <div>
                    <div>{record.username}</div>
                    <div style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', width: 220 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 4, flex: 1 }} title={record.userid}>
                            {record.userid}
                        </div>
                        <CopyOutlined
                            style={{ cursor: 'pointer', color: '#1890ff' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(record.userid);
                            }}
                        />
                    </div>
                </div>
            )
        },
        {
            title: '获客链接',
            key: 'link_info',
            render: (_, record) => (
                <div>
                    <div>{record.link_name}</div>
                    <div style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', width: 220 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 4, flex: 1 }} title={record.link_url}>
                            {record.link_url}
                        </div>
                        <CopyOutlined
                            style={{ cursor: 'pointer', color: '#1890ff' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(record.link_url);
                            }}
                        />
                    </div>
                </div>
            )
        },
        {
            title: '加粉总数',
            dataIndex: 'fans_total',
            key: 'fans_total',
        },
        {
            title: '开口人数',
            dataIndex: 'start_chat_total',
            key: 'start_chat_total',
        },
        {
            title: '开口率',
            dataIndex: 'start_chat_rate',
            key: 'start_chat_rate',
            render: (text) => {
                if (text === undefined || text === null) return '-';
                return (text * 100).toFixed(2) + '%';
            }
        },
        {
            title: '客服有效期',
            dataIndex: 'code_expire_time',
            key: 'code_expire_time',
            render: (val, record) => {
                const expireTime = Number(val);
                const licenseCheckTime = record.license_check_time;
                
                // Determine which time to display
                let timeToDisplay;
                let useLicenseTime = false;
                if (licenseCheckTime > 0) {
                    timeToDisplay = new Date(licenseCheckTime * 1000).toLocaleString();
                    useLicenseTime = true;
                } else if (expireTime > 0) {
                    timeToDisplay = new Date(expireTime * 1000).toLocaleString();
                } else {
                    timeToDisplay = '-';
                }
                
                // Only show exception if not in trial period
                const inTrial = isInTrialPeriod(licenseCheckTime);
                const isException = !inTrial && (!expireTime || expireTime <= 0 || (expireTime * 1000) <= Date.now());
                return (
                    <div>
                        <div>{timeToDisplay}</div>
                        {useLicenseTime && (
                            <Tag color="orange" style={{ marginTop: 4 }}>
                                在试用期内
                            </Tag>
                        )}
                        {isException && (
                            <Tag color="red" style={{ marginTop: 4 }}>
                                <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                                客服异常
                            </Tag>
                        )}
                    </div>
                );
            }
        },
        {
            title: '企业信息',
            key: 'corp_info',
            render: (_, record) => (
                <div>
                    <div>{record.corp_name}</div>
                    <div style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', width: 220 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 4, flex: 1 }} title={record.corp_id}>
                            {record.corp_id}
                        </div>
                        <CopyOutlined
                            style={{ cursor: 'pointer', color: '#1890ff' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(record.corp_id);
                            }}
                        />
                    </div>
                </div>
            )
        },
        {
            title: '所属部门',
            dataIndex: 'departments',
            key: 'departments',
            render: (departments) => {
                if (!departments) return '-';
                const depts = departments.split(',');
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {depts.map((dept, index) => (
                            <Tag color="blue" key={index}>{dept}</Tag>
                        ))}
                    </div>
                );
            }
        },

    ];

    return (
        <div className="customer-links-container">

            <div className="wecom-header">
                <span>获客分组</span>
                <div style={{ textAlign: 'right', marginTop: 16, marginBottom: 16 }}>
                    <Button type="primary" className='orange-button' onClick={handleCreateGroupClick}>创建获客分组</Button>
                </div>
            </div>

            <Card bordered={false} className="search-card">
                <Form form={groupForm} layout="inline" onFinish={handleGroupSearch}>
                    <Form.Item name="group_name">
                        <Input placeholder="获客分组名称" allowClear />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" className='orange-button' htmlType="submit">查询</Button>
                            <Button onClick={handleGroupReset}>重置</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            <Table
                columns={groupColumns}
                dataSource={groupData}
                rowKey="id"
                pagination={{
                    ...groupPagination,
                    showTotal: (total) => `共 ${total} 条`,
                    showSizeChanger: true,
                }}
                loading={groupLoading}
                onChange={handleGroupTableChange}
            />

            {/* Link List Drawer */}
            <Drawer
                // title="关联获客链接"
                title={`${currentGroupName} —— 关联获客链接`}
                width="80%"
                onClose={() => setLinkListDrawerVisible(false)}
                open={linkListDrawerVisible}
                extra={
                    <Button type="primary" className="orange-button" onClick={handleCreateClick}>
                        添加获客链接
                    </Button>
                }
            >
                <div className="customer-links-container" style={{ padding: 0 }}>
                    <Card bordered={false} className="search-card">
                        <Form
                            form={form}
                            layout="inline"
                            onFinish={onFinish}
                        >
                            <Form.Item name="username">
                                <Input placeholder="输入客服名称" allowClear style={{ width: 150 }} />
                            </Form.Item>
                            <Form.Item name="department">
                                <Input placeholder="输入所属部门" allowClear style={{ width: 150 }} />
                            </Form.Item>
                            <Form.Item>
                                <Space>
                                    <Button type="primary" className='orange-button' htmlType="submit" loading={loading}>
                                        查询
                                    </Button>
                                    <Button onClick={onReset}>重置</Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>

                    <Card bordered={false} style={{ marginTop: 16 }}>
                        <Table
                            columns={columns}
                            dataSource={data}
                            rowKey="id"
                            pagination={{
                                ...pagination,
                                showTotal: (total) => `共 ${total} 条`,
                                showSizeChanger: true,
                                showQuickJumper: true,
                            }}
                            loading={loading}
                            onChange={handleTableChange}
                        />
                    </Card>
                </div>
            </Drawer>

            {/* Create Link Drawer */}
            <Drawer
                title={`${currentGroupName} —— 添加获客链接`}
                width={1200}
                onClose={handleCreateClose}
                open={createDrawerVisible}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleCreateClose}>取消</Button>
                            <Button type="primary" onClick={handleCreateSubmit} loading={createLoading} className="orange-button">
                                保存
                            </Button>
                        </Space>
                    </div>
                }
            >
                <div>
                    <Row gutter={24} style={{ border: '1px solid #f0f0f0', borderRadius: 4 }}>
                        <Col span={8} style={{ borderRight: '1px solid #f0f0f0', padding: 0 }}>
                            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                                <Input.Search
                                    placeholder="输入企业名称"
                                    onSearch={val => fetchCreateCorpList(1, val)}
                                    onChange={e => setCreateCorpSearchName(e.target.value)}
                                    allowClear
                                />
                            </div>
                            <div style={{ height: 500, overflowY: 'auto' }}>
                                <List
                                    dataSource={createCorpList}
                                    loading={createCorpLoading}
                                    renderItem={item => {
                                        const selectedCount = (selectedUsersMap[item.corp_id] || []).length;
                                        const hasError = !!item.customer_assistant_errcode;
                                        const isSelected = selectedCorpId === item.corp_id;
                                        return (
                                            <List.Item
                                                className={`corp-list-item ${isSelected ? 'active' : ''}`}
                                                onClick={() => !hasError && handleCorpSelect(item.corp_id)}
                                                style={{
                                                    cursor: hasError ? 'not-allowed' : 'pointer',
                                                    padding: '12px 16px',
                                                    background: isSelected ? '#e6f7ff' : (hasError ? '#f5f5f5' : 'transparent'),
                                                    opacity: hasError ? 0.6 : 1
                                                }}
                                            >
                                                <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.corp_name}</div>
                                                        <div style={{ color: '#999', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.corp_id}</div>
                                                        {hasError && (
                                                            <Tag color="red" style={{ marginTop: 4 }}>
                                                                <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                                                                企业资质不全
                                                            </Tag>
                                                        )}
                                                    </div>
                                                    {selectedCount > 0 && <Tag color="orange">已选 {selectedCount}</Tag>}
                                                </div>
                                            </List.Item>
                                        );
                                    }}
                                />
                            </div>
                            <div style={{ padding: '8px 16px', borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
                                <Pagination
                                    simple
                                    current={createCorpPage}
                                    pageSize={20}
                                    total={createCorpTotal}
                                    onChange={(page) => fetchCreateCorpList(page, createCorpSearchName)}
                                />
                            </div>
                        </Col>

                        <Col span={16} style={{ padding: 0 }}>
                            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontWeight: 500 }}>
                                    {selectedCorpId ? (createCorpList?.find(c => c.corp_id === selectedCorpId)?.corp_name || '未找到企业') : '请选择企业'}
                                </div>
                                <Input.Search
                                    placeholder="输入成员名称"
                                    style={{ width: 200 }}
                                    onSearch={val => fetchCreateEmployeeList(selectedCorpId, 1, val)}
                                    onChange={e => setCreateEmployeeSearchName(e.target.value)}
                                    disabled={!selectedCorpId}
                                    allowClear
                                />
                            </div>
                            <div style={{ height: 500, overflowY: 'auto', padding: 0 }}>
                                {selectedCorpId ? (
                                    <>
                                        <div style={{ display: 'flex', padding: '12px 16px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>
                                            <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>客服信息</div>
                                            <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>客服有效期</div>
                                            <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>所属部门</div>
                                        </div>
                                        <List
                                            dataSource={createEmployeeList}
                                            loading={createEmployeeLoading}
                                            renderItem={item => {
                                                const expireTime = Number(item.code_expire_time);
                                                // Get selected corp's license_check_time
                                                const selectedCorp = createCorpList.find(c => c.corp_id === selectedCorpId);
                                                const licenseCheckTime = selectedCorp?.license_check_time;
                                                
                                                // Determine which time to display
                                                let timeToDisplay;
                                                let useLicenseTime = false;
                                                if (licenseCheckTime > 0) {
                                                    timeToDisplay = new Date(licenseCheckTime * 1000).toLocaleString();
                                                    useLicenseTime = true;
                                                } else if (expireTime > 0) {
                                                    timeToDisplay = new Date(expireTime * 1000).toLocaleString();
                                                } else {
                                                    timeToDisplay = '-';
                                                }
                                                
                                                // Note: Removed customer service expiration restrictions on create page per requirement
                                                return (
                                                    <List.Item
                                                        style={{
                                                            padding: '12px 16px',
                                                            cursor: 'pointer',
                                                            borderBottom: '1px solid #f0f0f0',
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            background: 'transparent',
                                                            opacity: 1
                                                        }}
                                                        onClick={() => toggleUserSelection(item.userid, selectedCorpId)}
                                                    >
                                                        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', paddingTop: '2px' }}>
                                                            <Checkbox
                                                                checked={(selectedUsersMap[selectedCorpId] || []).includes(item.userid)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={() => toggleUserSelection(item.userid, selectedCorpId)}
                                                            />
                                                            <div style={{ marginLeft: 12, flex: 1, minWidth: 0 }}>
                                                                <div>{item.name}</div>
                                                                <div style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center' }}>
                                                                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 4, flex: 1 }} title={item.userid}>
                                                                        {item.userid}
                                                                    </div>
                                                                    <CopyOutlined
                                                                        style={{ cursor: 'pointer', color: '#1890ff', flexShrink: 0 }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            copyToClipboard(item.userid);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: '4px', paddingTop: '2px' }}>
                                                            <div style={{ fontSize: '14px', minHeight: '22px', display: 'flex', alignItems: 'center' }}>{timeToDisplay}</div>
                                                            {useLicenseTime && (
                                                                <Tag color="orange" style={{ margin: 0 }}>在试用期内</Tag>
                                                            )}
                                                        </div>

                                                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'flex-start', paddingTop: '2px', alignItems: 'center' }}>
                                                            {item.department_names && item.department_names.split(',').map((dept, i) => (
                                                                <Tag key={i} color="blue" style={{ marginRight: 0, width: 'fit-content' }}>{dept}</Tag>
                                                            ))}
                                                        </div>
                                                    </List.Item>
                                                )
                                            }}
                                        />
                                    </>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>请先选择左侧企业</div>
                                )}
                            </div>
                            {selectedCorpId && (
                                <div style={{ padding: '8px 16px', borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
                                    <Pagination
                                        simple
                                        current={createEmployeePage}
                                        pageSize={20}
                                        total={createEmployeeTotal}
                                        onChange={(page) => fetchCreateEmployeeList(selectedCorpId, page, createEmployeeSearchName)}
                                    />
                                </div>
                            )}
                        </Col>
                    </Row>
                </div>
            </Drawer>

            {/* Update Name Modal */}
            <Modal
                title="修改链接名称"
                open={updateModalVisible}
                onOk={handleUpdateSubmit}
                onCancel={() => setUpdateModalVisible(false)}
                confirmLoading={updateLoading}
                okText="保存"
                cancelText="取消"
            >
                <Form form={updateForm} layout="vertical">
                    <Form.Item
                        name="link_name"
                        label="获客链接名称"
                        rules={[{ required: true, message: '请输入获客链接名称' }]}
                    >
                        <Input placeholder="请输入获客链接名称" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Detail Drawer */}
            <Drawer
                title="获客链接详情"
                width={720}
                onClose={() => setDetailDrawerVisible(false)}
                open={detailDrawerVisible}
            >
                {detailLoading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>加载中...</div>
                ) : detailData ? (
                    <div>
                        <div style={{ marginBottom: 24 }}>
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ color: '#999', marginBottom: 8 }}>所属企业</div>
                                <div style={{ fontSize: '16px' }}>{detailData.corp_name || '-'}</div>
                                <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>{detailData.corp_id || '-'}</div>
                            </div>

                            <div style={{ marginBottom: 16 }}>
                                <div style={{ color: '#999', marginBottom: 8 }}>获客链接名称</div>
                                <div style={{ fontSize: '16px' }}>{detailData.link_name || '-'}</div>
                            </div>

                            <div style={{ marginBottom: 16 }}>
                                <div style={{ color: '#999', marginBottom: 8 }}>获客链接ID</div>
                                <div style={{ fontSize: '14px' }}>{detailData.link_id || '-'}</div>
                            </div>

                            <div style={{ marginBottom: 16 }}>
                                <div style={{ color: '#999', marginBottom: 8 }}>获客链接URL</div>
                                <div style={{ fontSize: '14px' }}>
                                    {detailData.link_url ? (
                                        <a href={detailData.link_url} target="_blank" rel="noopener noreferrer">
                                            {detailData.link_url}
                                        </a>
                                    ) : '-'}
                                </div>
                            </div>

                            <div style={{ marginBottom: 16 }}>
                                <div style={{ color: '#999', marginBottom: 8 }}>创建时间</div>
                                <div style={{ fontSize: '14px' }}>{detailData.create_at || '-'}</div>
                            </div>
                        </div>

                        <div>
                            <div style={{ color: '#999', marginBottom: 12, fontSize: '16px', fontWeight: 500 }}>接粉客服列表</div>
                            {detailData.users && detailData.users.length > 0 ? (
                                <Table
                                    dataSource={detailData.users}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    columns={[
                                        {
                                            title: '客服ID',
                                            dataIndex: 'userid',
                                            key: 'userid',
                                        },
                                        {
                                            title: '当前状态',
                                            dataIndex: 'status',
                                            key: 'status',
                                            render: (status) => (
                                                <span style={{ color: status === '有效' ? '#52c41a' : '#ff4d4f' }}>
                                                    {status || '-'}
                                                </span>
                                            ),
                                        },
                                    ]}
                                />
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>暂无客服数据</div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>暂无数据</div>
                )}
            </Drawer>

            {/* Create/Edit Group Modal */}
            <Modal
                title={currentGroup ? "修改获客分组" : "创建获客分组"}
                open={createGroupModalVisible || editGroupModalVisible}
                onOk={handleGroupSubmit}
                onCancel={() => {
                    setCreateGroupModalVisible(false);
                    setEditGroupModalVisible(false);
                }}
                confirmLoading={groupModalLoading}
                okButtonProps={{ className: 'orange-button' }}
            >
                <Form form={groupEditForm} layout="vertical">
                    <Form.Item
                        name="group_name"
                        label="获客分组名称"
                        style={{ marginTop: '20px' }}
                        rules={[{ required: true, message: '请输入获客分组名称' }]}
                    >
                        <Input placeholder="请输入获客分组名称" />
                    </Form.Item>
                    <Form.Item name="remark" label="备注信息">
                        <Input.TextArea placeholder="请输入备注信息" />
                    </Form.Item>
                </Form>
            </Modal>
        </div >
    );
};

export default CustomerLinksList;
